import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { searchPharmacies } from '../../utils/googlePlaces';
import { convertTimestampsToISO } from '../../utils/firebaseUtils.js';
import {
  getAllPharmacies as getAllPharmaciesService,
  createPharmacy as createPharmacyService,
  updatePharmacy as updatePharmacyService
} from '../../services/pharmacyService';

// Async thunks
export const fetchPharmacies = createAsyncThunk(
  'pharmacy/fetchPharmacies',
  async (facilityId, { getState, rejectWithValue }) => {
    try {
      // Use passed facilityId, or fall back to state
      let effectiveFacilityId = facilityId;
      if (!effectiveFacilityId) {
        const state = getState();
        const defaultFacility = state.auth.user?.defaultFacility;
        effectiveFacilityId = typeof defaultFacility === 'object' ? defaultFacility?.id : defaultFacility;
      }
      
      if (!effectiveFacilityId) {
        throw new Error('No default facility found for user');
      }
      
      const pharmacies = await getAllPharmaciesService(effectiveFacilityId);
      return pharmacies.map(pharmacy => convertTimestampsToISO(pharmacy));
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
  {
    // Prevent duplicate fetches if already loading
    condition: (_, { getState }) => {
      const { pharmacy } = getState();
      if (pharmacy.status === 'loading') {
        return false; // Cancel the thunk
      }
      return true;
    }
  }
);

export const addPharmacy = createAsyncThunk(
  'pharmacy/addPharmacy',
  async (pharmacyData, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const defaultFacility = state.auth.user?.defaultFacility;
      const facilityId = typeof defaultFacility === 'object' ? defaultFacility?.id : defaultFacility;
      
      if (!facilityId) {
        throw new Error('No default facility found for user');
      }
      
      const pharmacy = await createPharmacyService({
        ...pharmacyData,
        facilityId, // Ensure facilityId is set
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      return pharmacy;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePharmacy = createAsyncThunk(
  'pharmacy/updatePharmacy',
  async ({ id, data }) => {
    const updateData = {
      ...data,
      updatedAt: new Date().toISOString()
    };
    const pharmacy = await updatePharmacyService(id, updateData);
    return pharmacy;
  }
);

export const searchPharmaciesOnline = createAsyncThunk(
  'pharmacy/searchPharmaciesOnline',
  async (searchText, { rejectWithValue }) => {
    try {
      const results = await searchPharmacies(searchText);
      return results;
    } catch (error) {
      console.error('Search error:', error);
      return rejectWithValue(error.message);
    }
  }
);

const pharmacySlice = createSlice({
  name: 'pharmacy',
  initialState: {
    pharmacies: [],
    searchResults: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    searchStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    searchError: null
  },
  reducers: {
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchStatus = 'idle';
      state.searchError = null;
    }
  },
  extraReducers: (builder) => {
    // Search reducers
    builder
      .addCase(searchPharmaciesOnline.pending, (state) => {
        state.searchStatus = 'loading';
        state.searchError = null;
      })
      .addCase(searchPharmaciesOnline.fulfilled, (state, action) => {
        state.searchStatus = 'succeeded';
        state.searchResults = action.payload;
        state.searchError = null;
      })
      .addCase(searchPharmaciesOnline.rejected, (state, action) => {
        state.searchStatus = 'failed';
        state.searchError = action.payload || 'Failed to search pharmacies';
        state.searchResults = [];
      });

    // Fetch reducers
    builder
      .addCase(fetchPharmacies.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPharmacies.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.pharmacies = action.payload;
        state.error = null;
      })
      .addCase(fetchPharmacies.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch pharmacies';
      });

    // Add pharmacy reducers
    builder
      .addCase(addPharmacy.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(addPharmacy.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.pharmacies.push(action.payload);
        state.error = null;
      })
      .addCase(addPharmacy.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to add pharmacy';
      });

    // Update pharmacy reducers
    builder
      .addCase(updatePharmacy.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updatePharmacy.fulfilled, (state, action) => {
        state.status = 'succeeded';
        const index = state.pharmacies.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.pharmacies[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updatePharmacy.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || 'Failed to update pharmacy';
      });
  }
});

export const { clearSearchResults } = pharmacySlice.actions;
export default pharmacySlice.reducer;
